import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import type { ProductVariantMaterialsSummaryDto } from '../../../viewmodels/useProductPricing'

interface Props {
  readonly open: boolean
  readonly productId: string | null
  readonly productName: string
  readonly onClose: () => void
  readonly listProductVariants: (
    productId: string
  ) => Promise<{ ok: true; variants: ProductVariantMaterialsSummaryDto[] } | { ok: false; message: string }>
  readonly cloneMaterials: (
    sourceVariantId: string
  ) => Promise<{ ok: true; clonedCount: number } | { ok: false; message: string }>
  readonly onCloned: (clonedCount: number) => void
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; variants: ProductVariantMaterialsSummaryDto[] }
  | { status: 'error'; message: string }

function variantLabel(v: ProductVariantMaterialsSummaryDto): string {
  const base = v.axes_label === null || v.axes_label === '' ? 'default' : v.axes_label
  return `${base} — ${v.material_count} material${v.material_count === 1 ? '' : 's'}`
}

export function CloneMaterialsModal({
  open,
  productId,
  productName,
  onClose,
  listProductVariants,
  cloneMaterials,
  onCloned
}: Props): JSX.Element {
  const [state, setState] = useState<LoadState | null>(null)
  const [sourceId, setSourceId] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || productId === null) {
      setState(null)
      setSourceId('')
      setSubmitting(false)
      setFormError(null)
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    void listProductVariants(productId).then((res) => {
      if (cancelled) return
      if (res.ok) setState({ status: 'ready', variants: res.variants })
      else setState({ status: 'error', message: res.message })
    })
    return () => {
      cancelled = true
    }
  }, [open, productId, listProductVariants])

  const variants = state?.status === 'ready' ? state.variants : []
  const source = variants.find((v) => v.variant_id === sourceId) ?? null
  const tooFewVariants = state?.status === 'ready' && variants.length < 2
  const sourceHasNoMaterials = source !== null && source.material_count === 0
  const canClone =
    state?.status === 'ready' && source !== null && !sourceHasNoMaterials && !tooFewVariants && !submitting

  const handleClone = async (): Promise<void> => {
    if (source === null) return
    setFormError(null)
    setSubmitting(true)
    const res = await cloneMaterials(source.variant_id)
    setSubmitting(false)
    if (res.ok) {
      onCloned(res.clonedCount)
      return
    }
    setFormError(res.message)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Clone materials — ${productName}`}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleClone()} disabled={!canClone}>
            {submitting ? 'Cloning…' : 'Clone'}
          </Button>
        </>
      }
    >
      {state === null || state.status === 'loading' ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : state.status === 'error' ? (
        <Alert tone="error">{state.message}</Alert>
      ) : (
        <div className="space-y-4">
          {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

          {tooFewVariants ? (
            <Alert tone="info">
              This product has only one variant — there are no other variants to clone to.
            </Alert>
          ) : null}

          <div className="space-y-1.5">
            <label htmlFor="clone-source" className="block text-body-sm font-medium text-charcoal">
              Copy materials from
            </label>
            <Select
              id="clone-source"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              disabled={tooFewVariants}
            >
              <option value="">Select a variant…</option>
              {variants.map((v) => (
                <option key={v.variant_id} value={v.variant_id}>
                  {variantLabel(v)}
                </option>
              ))}
            </Select>
            {sourceHasNoMaterials ? (
              <span className="block text-body-xs text-terra">
                This variant has no materials to clone.
              </span>
            ) : null}
          </div>

          <p className="text-body-sm text-text-secondary">
            The materials and cost of the selected variant will be copied to{' '}
            <span className="font-medium">all other variants</span> of {productName},{' '}
            <span className="font-medium">overwriting</span> their current materials.
          </p>
        </div>
      )}
    </Modal>
  )
}
